<template>
  <div class="intro-y box p-5">
    <!-- Question field:start -->
    <div class="mt-6">
      <label :for="`form-question-${kActive}`" class="form-label">
        {{ $t(`${viewRoot}.form.question.label`) }}
      </label>

      <div class="input-group">
        <input
          :id="`form-question-${kActive}`"
          type="text"
          class="form-control w-full bg-theme-32"
          :placeholder="$t(`${viewRoot}.form.question.placeholder`)"
          v-model="localData.langs[kActive].question"
          :disabled="readOnly"
          @input="checkForbidden('question', localData.langs[kActive].question)"
        />
      </div>
      <div v-if="hasForbidden('question')" class="text-theme-6 mt-2">
        {{ $t("views.forbidden") }}
        <strong>{{ getForbidden("question").join(", ") }}</strong>
      </div>

      <div v-if="localData.validations.question" class="text-theme-6 mt-2">
        {{ localData.validations.question }}
      </div>
    </div>
    <!-- Question field:end -->

    <!-- Feedback field:start -->
    <div class="mt-6">
      <label :for="`form-feedback-${kActive}`" class="form-label">
        {{ $t(`${viewRoot}.form.feedback.label`) }}
      </label>

      <div class="input-group">
        <input
          :id="`form-feedback-${kActive}`"
          type="text"
          class="form-control w-full bg-theme-32"
          :placeholder="$t(`${viewRoot}.form.feedback.placeholder`)"
          v-model="localData.langs[kActive].feedback"
          :disabled="readOnly"
          @input="checkForbidden('feedback', localData.langs[kActive].feedback)"
        />
      </div>

      <div v-if="localData.validations.feedback" class="text-theme-6 mt-2">
        {{ localData.validations.feedback }}
      </div>
      <!-- Palabras prohibidas -->
      <div v-if="hasForbidden('feedback')" class="text-theme-6 mt-2">
        {{ $t("views.forbidden") }}
        <strong>{{ getForbidden("feedback").join(", ") }}</strong>
      </div>
    </div>
    <!-- Feedback field:end -->

    <!-- Difficulty Level field:start -->
    <div class="flex items-center mt-6 w-1/2">
      <label class="w-2/5 form-label text-nowrap text-nowrap-mode">
        {{ $t(`${viewRoot}.form.difficulty.label`) }}
      </label>

      <TomSelect
        v-model="difficultyProxy"
        class="w-2/5 cursor"
        :placeholder="$t(`${viewRoot}.form.difficulty.placeholder`)"
        :disabled="disabledLevelDifficulty || readOnly"
      >
        <option v-for="(level, idx) in difficultyLevels" :key="`difficulty-${idx}`" :value="level">
          {{ $t(`${viewRoot}.form.difficulty.option`, { level }) }}
        </option>
      </TomSelect>
    </div>
    <!-- Difficulty Level field:end -->

    <hr class="my-6" />

    <!-- Answers field:start -->
    <div class="mt-3">
      <div class="flex justify-between items-center">
        <h2>{{ $t(`${viewRoot}.form.answer.label`) }}</h2>

        <button v-if="!readOnly" type="button" class="intro-x btn btn-primary" @click="addAnswer">
          <PlusIcon class="mr-2" />
          <span>{{ $t(`${viewRoot}.form.add_answer`) }}</span>
        </button>
      </div>

      <div class="intro-y col-span-12 overflow-auto mt-5">
        <table class="table table-report -mt-2 overflow-hidden">
          <thead>
            <tr>
              <th class="whitespace-nowrap">#</th>
              <th class="whitespace-nowrap">
                {{ $t(`${viewRoot}.form.table.answer`) }}
              </th>
              <th class="whitespace-nowrap text-center">
                {{ $t(`${viewRoot}.form.table.correct`) }}
              </th>
              <th v-if="!readOnly" class="whitespace-nowrap text-center">
                {{ $t(`${viewRoot}.form.table.actions`) }}
              </th>
            </tr>
          </thead>

          <tbody>
            <template v-for="(ans, idx) in localData.answers" :key="idx">
              <tr class="intro-x animation__row" :class="{ disabled__row: ans.disabled }">
                <!-- Index -->
                <td>{{ idx + 1 }}</td>

                <!-- Answer -->
                <td class="w-full">
                  <input
                    :id="`form-answer-${kActive}-${idx}`"
                    type="text"
                    class="form-control w-full bg-theme-32"
                    :placeholder="$t(`${viewRoot}.form.answer.placeholder`)"
                    v-model="ans.langs[kActive].answer"
                    :disabled="ans.disabled || readOnly"
                    @input="checkForbidden(`answer_${idx}`, ans.langs[kActive].answer)"
                  />

                  <div v-if="ans.validations.answer && !ans.disabled" class="text-theme-6 mt-2">
                    {{ ans.validations.answer }}
                  </div>
                  <!-- Forbidden words -->
                  <div v-if="hasForbidden(`answer_${idx}`) && !ans.disabled" class="text-theme-6 mt-2">
                    {{ $t("views.forbidden") }}
                    <strong>{{ getForbidden(`answer_${idx}`).join(", ") }}</strong>
                  </div>
                </td>

                <!-- Correct -->
                <td class="text-center">
                  <input
                    type="checkbox"
                    class="form-check-switch"
                    v-model="ans.is_correct"
                    :disabled="ans.disabled || readOnly"
                  />
                </td>

                <!-- Actions -->
                <td v-if="!readOnly" class="text-center">
                  <div class="flex justify-center items-center gap-2">
                    <!-- 1st click: disables -->
                    <button
                      v-if="!ans.disabled"
                      @click="removeAnswer(idx)"
                      type="button"
                      class="btn-outline-dark btn-rounded btn-sm"
                    >
                      <Trash2Icon />
                    </button>

                    <!-- 2nd click: delete / restore -->
                    <div v-else class="flex">
                      <button @click="removeAnswer(idx)" type="button" class="btn-outline-dark btn-rounded btn-sm">
                        <Trash2Icon />
                      </button>
                      <button
                        @click="removeAnswer(idx, false)"
                        type="button"
                        class="btn-outline-dark btn-rounded btn-sm"
                        title="Restaurar"
                      >
                        <RotateCcwIcon />
                      </button>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>

        <div v-if="localData.validations.minAnswer" class="text-theme-6 mt-2">
          {{ localData.validations.minAnswer }}
        </div>
      </div>
    </div>
    <!-- Answers field:end -->
  </div>
</template>

<script>
import MiniGuidesService from "@/services/mini_guide.service";

import { ensureShape, buildAnswerLangs, buildAnswerValidations } from "@/utils/modules/ilongame/questionWithAnswers";

import { checkForbidden, hasForbidden, getForbidden } from "@/utils/forbidden_terms";

export default {
  name: "QuestionWithAnswersModule",
  props: {
    modelValue: { type: Object, required: true },
    langs: { type: Array, required: true },
    activeLangKey: { type: String, default: "" },
    forbiddenTermsProps: { type: [Array, Object], default: null },
    disabledLevelDifficulty: { type: Boolean, default: false },
    readOnly: { type: Boolean, default: false },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      viewRoot: "views.ilongame.questionsWithAnswers",
      minAnswers: 4,
      difficultyLevels: [1, 2, 3, 4, 5],
      localData: ensureShape(this.modelValue, this.langs, { minAnswers: 4 }),
      syncingModel: false,
      forbidden: {},
      forbiddenTerms: [],
    };
  },
  mounted() {
    this.loadForbiddenTerms();
  },
  computed: {
    kActive() {
      return this.activeLangKey || (localStorage.lang ? localStorage.lang : navigator.language.split("-")[0]);
    },
    difficultyProxy: {
      get() {
        return String(this.localData.difficulty_level ?? 1);
      },
      set(v) {
        const n = Number(v);
        this.localData.difficulty_level = Number.isFinite(n) ? n : 1;
      },
    },
  },
  watch: {
    modelValue: {
      deep: true,
      handler(val) {
        if (this.syncingModel) return;
        const next = ensureShape(val, this.langs, { minAnswers: this.minAnswers });
        if (JSON.stringify(next) !== JSON.stringify(this.localData)) {
          this.localData = next;
        }
      },
    },
    localData: {
      deep: true,
      handler(val) {
        this.syncingModel = true;
        this.$emit("update:modelValue", JSON.parse(JSON.stringify(val)));
        this.$nextTick(() => {
          this.syncingModel = false;
        });
      },
    },
  },

  methods: {
    /**
     * * Forbidden words methods
     */
    checkForbidden(fieldKey, value) {
      this.forbidden = checkForbidden(this.forbidden, fieldKey, value, this.forbiddenTerms);
    },
    hasForbidden(fieldKey) {
      return hasForbidden(this.forbidden, fieldKey);
    },
    getForbidden(fieldKey) {
      return getForbidden(this.forbidden, fieldKey);
    },
    async loadForbiddenTerms() {
      try {
        const response = this.forbiddenTermsProps ?? (await MiniGuidesService.getForbiddenTerms());

        // Detects if the response is a direct array or wrapped in data
        const raw = Array.isArray(response) ? response : Array.isArray(response?.data) ? response.data : [];

        this.forbiddenTerms = raw.map((term) => String(term).trim());
      } catch (error) {
        this.forbiddenTerms = [];
      }
    },

    /**
     * * Answers methods
     */
    addAnswer() {
      this.localData.answers.push({
        id: null,
        ilongame_question_id: this.modelValue?.ilongame_question_id ?? null,
        is_correct: false,
        disabled: false,
        langs: buildAnswerLangs(this.langs),
        validations: buildAnswerValidations(),
      });
    },
    removeAnswer(index, disable = true) {
      if (index < 0 || this.localData.answers.length <= 1) return;
      const item = this.localData.answers[index];
      if (!item) return;
      if (disable && item.disabled) this.localData.answers.splice(index, 1);
      else item.disabled = disable;
    },
  },
};
</script>

<style lang="scss" scoped>
.dark .form-check-switch:checked {
  background-color: rgba(41, 49, 69, 1);
  border-color: rgba(63, 72, 101, 1);

  &::before {
    background-color: #c4e8fd;
  }
}

.disabled__row {
  opacity: 0.5 !important;
}

.text-nowrap-mode {
  white-space: nowrap;
}
</style>
